import BigNumber from 'bignumber.js'
import React, { useState, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import { Button, Heading, Text, Link, Flex, LinkExternal } from '@pancakeswap-libs/uikit'
import useI18n from 'hooks/useI18n'
import {CHAINS, switchChain} from 'utils/wallet'
import { Config } from 'utils/bridgeUtils'
import { useChainId, useConnectedAddress } from 'hooks/useWeb3'
import Select from 'components/SelectInput'
import SearchSelect from 'components/SearchSelectModal/SearchSelect'
import BalanceInput from 'components/Input/BalanceInput'
import { OptionProps } from 'components/SelectInput/SelectInput'
import { useApprove, useBalance, useClaim, useDepositTokens, useTokenList, useTokenAddress } from '../hooks/useBridge'

const StyledContainer = styled.div`
  text-align: center;
  position: relative;
  padding-top: 20px;
`;

const Content = styled.div`
  display: inline-block;
  background: ${({ theme }) => (theme.isDark ? 'rgb(44, 41, 38)' : 'white')};
  box-shadow: 2px 2px 8px ${({ theme }) => (theme.isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)')};
  border-radius: 32px;
  padding: 30px 25px;
  position: relative;
  width: 600px;

  @media (max-width: 700px) {
    width: 100%;
    padding: 30px 15px;
  }
`

const Row = styled.div`
    margin-top: 25px;
`;

const Label = styled(Text)`
  text-align: left;
  padding-left: 16px;
  margin-bottom: 5px;
`

const AddressContainer = styled(Text)`
  text-align: left;
`

const ErrorRow = styled(Text)`
  text-align: center;
  color: #ff6a6a;
  margin-top: 5px;
`

const StyledLinkExternl = styled(LinkExternal)`
  display: inline-flex;
  margin-left: 5px;
`

const Chains = Object.keys(Config.chains).map(c=>Number(c));
const ChianIndex:any = {};
Chains.forEach((c,i)=>{ChianIndex[c]=i});
const ChainOptions = Chains.map(c=>{
  const info = Config.chains[c];
  return {
    label: info.name,
    value: c,
    img: { 
      url: `/images/bridge/chains/${info.symbol}.png`, 
      name: info.symbol
    }
  };
});
const getChainShortName = (chainId) => {
  const info = Config.chains[chainId];
  return info?.shortName || "";
}

const getDisplayBalance = (balance) => {
  const displayBalanceOption = balance > 1 ? {} : {maximumFractionDigits: 12};
  const displayBalance = navigator ? balance.toLocaleString(navigator.language, displayBalanceOption) : balance.toLocaleString();
  return displayBalance;
}

const Core = () => {
  const TranslateString = useI18n();
  const chainId = useChainId();
  const connectedAddress = useConnectedAddress();
  const balances = useBalance(connectedAddress);

  const [fromChainId, setFromChainId] = useState(56);
  const [toChainId, setToChainId] = useState(10000);
  const [tokenSymbol, setTokenSymbol] = useState("EBEN");
  const tokenList = useTokenList(fromChainId, toChainId);

  const chainBalances = balances[fromChainId];
  const getChainDisplayBalance = (symbol) => {
    const n = chainBalances && chainBalances[symbol] && chainBalances[symbol].balance.toNumber();
    return n ? getDisplayBalance(n) : "0";
  }
  
  const [amount, setAmount] = useState("")
  const [error, setError] = useState("");

  const fromChainExplorer = CHAINS[fromChainId]?.blockExplorerUrls[0];
  const toChainExplorer = CHAINS[toChainId]?.blockExplorerUrls[0];
  const fromChainTokenAddress = useTokenAddress(fromChainId, tokenSymbol);

  const options = tokenList.map(t=>{
    return {
      name: `${t} ${connectedAddress ? `(${getChainDisplayBalance(t)})`:""}`,
      image: `/images/bridge/${t}.png`,
      id: t
    };
  });
  const selected = options.find(p=>p.id===tokenSymbol) || options[0];
  const handleOptionChange = (option) => {
    setTokenSymbol(option.id);
  }

  useEffect(()=>{
      setAmount("");
      setError("");
    } , 
    // eslint-disable-next-line
    [fromChainId, connectedAddress, tokenSymbol]
  )

  const handleFromChainChange = (option: OptionProps): void => {
    const s=option.value;
    setFromChainId(s);
    if (toChainId === s) {
      setToChainId(Chains.find(c=>c!==s));
    }
  }

  const handleToChainChange = (option: OptionProps): void => {
    const s=option.value;
    setToChainId(s);
    if (fromChainId === s) {
      setFromChainId(Chains.find(c=>c!==s));
    }
  }

  const handleTokenChange = (option: OptionProps): void => {
    const s=option.value;
    setTokenSymbol(s);
  }

  const renderFromChainSelect = () => {
    return (
      <div>
        <Label>{TranslateString(2028,'From: ')}</Label>
        <Flex style={{height:"72px"}}>
          <Select options={ChainOptions} onOptionChange={handleFromChainChange} selectIndex={ChianIndex[fromChainId]}/>
        </Flex>
      </div>
    );
  }

  const renderToChainSelect = () => {
    return (
      <Row>
        <Label>{TranslateString(2029,'To: ')}</Label>
        <Flex style={{height:"72px"}}>
          <Select options={ChainOptions} onOptionChange={handleToChainChange} selectIndex={ChianIndex[toChainId]}/>
        </Flex>
      </Row>
    );
  }

  const renderTokenSelect = () => {
    return (
      <Row>
        <Label>{TranslateString(2030,'Token: ')}</Label>
        <Flex style={{height:"72px"}}>
          <SearchSelect selected={selected} options={options} onSelect={handleOptionChange}/>
        </Flex>
      </Row>
    );
  }

  const handleAmountChange = (e: React.FormEvent<HTMLInputElement>) => {
    setError("");
    setAmount(e.currentTarget.value);
  }
  const curBalance = chainBalances && chainBalances[tokenSymbol] && chainBalances[tokenSymbol].balance;
  const hitMaxBalance = chainBalances && chainBalances[tokenSymbol] && (chainBalances[tokenSymbol].maxBalance || chainBalances[tokenSymbol].balance);
  const balanceDisplay = (curBalance)?.toFixed() || "0";

  const renderAddresses = () => {
    return connectedAddress ? (
        <Row>
            {
              fromChainTokenAddress && !new BigNumber(fromChainTokenAddress).isLessThan(32) ? (
                <Label>
                  {TranslateString(2041,'Token: ')}
                  <StyledLinkExternl bold={false} href={`${fromChainExplorer}address/${fromChainTokenAddress}`}>{`${fromChainTokenAddress.substring(0,8)}...${fromChainTokenAddress.substring(36)}`}</StyledLinkExternl>
                </Label>
              ) : ""
            }
            <Label>
              {TranslateString(2040,'Wallet: ')}
              <StyledLinkExternl bold={false} href={`${fromChainExplorer}address/${connectedAddress}`}>{`${connectedAddress.substring(0,8)}...${connectedAddress.substring(36)}`}</StyledLinkExternl>
            </Label>
        </Row>
    ) : "";
  }

  const renderAmount = () => {
    return connectedAddress ? (
        <Row>
            <Label>{TranslateString(2031,'Amount: ')}</Label>
            <BalanceInput
                value={amount}
                onChange={handleAmountChange}
                symbol={tokenSymbol}
                max={balanceDisplay}
                onSelectMax={() => setAmount((hitMaxBalance)?.toFixed() || "0")}
            />
        </Row>
    ) : "";
  }

  const [requested, setRequested] = useState(false);
  const [approved, setApproved] = useState("")
  const alllowance = chainBalances && chainBalances[tokenSymbol] && chainBalances[tokenSymbol].allowance_r || new BigNumber(0);
  const onApprove = useApprove(fromChainId, connectedAddress, tokenSymbol);

  const [processedTransfer, setProcessedTransfer] = useState<any>({});
  const onDeposit = useDepositTokens(fromChainId, toChainId, connectedAddress, tokenSymbol);
  const onClaim = useClaim(processedTransfer?.fromChainId, processedTransfer?.toChainId, processedTransfer?.txId, connectedAddress);

  const onAnyRequestStart = () => {
    setError("");
    setRequested(true);
  }

  const renderTx = () => {
    return processedTransfer && processedTransfer.txId ? (
        <Row>
            <Label>
              {TranslateString(2042,'Transaction Hash:')}
              <StyledLinkExternl bold={false} href={`${CHAINS[processedTransfer.fromChainId]?.blockExplorerUrls[0]}tx/${processedTransfer.txId}`}>{`${processedTransfer.txId.substring(0,8)}...${processedTransfer.txId.substring(60)}`}</StyledLinkExternl>
            </Label>
        </Row>
    ) : "";
  }

  const handleApprove = useCallback(async () => {
      try {
        onAnyRequestStart()
        const txHash = await onApprove()
        setRequested(false)
        if (txHash) {
          setApproved(approved + 1);
        }
      } catch (e) {
          console.error(e)
      }
  }, [onApprove, approved])

  const renderButton = () => {
    if (processedTransfer && processedTransfer.txId) {
      // Processed transaction
      if (chainId !== processedTransfer.toChainId) {
        return (
          <Button disabled={requested} onClick={async () => {
            try {
              onAnyRequestStart()
              await switchChain(processedTransfer.toChainId);
              setRequested(false)
            } catch (e) {
                console.error(e)
            }
          }}>
            {TranslateString(2032,'Switch to ')}{getChainShortName(processedTransfer.toChainId)}{TranslateString(2033,' Network')}
          </Button>
        )
      }

      return (
        <Button disabled={requested} onClick={async()=>{
          try {
            onAnyRequestStart()
            const result = await onClaim();
            if (result && result.isSuccess) {
              setProcessedTransfer({});
            } else {
              setError(result.message)
            }
            setRequested(false)
          } catch (e) {
              console.error(e)
          }
        }}>
          {TranslateString(2034,'Claim')}
        </Button>
      );
    }

    if (chainId !== fromChainId) {
      return (
        <Button disabled={requested} onClick={async () => {
          try {
            onAnyRequestStart()
            await switchChain(fromChainId);
            setRequested(false)
          } catch (e) {
              console.error(e)
          }
        }}>
          {TranslateString(2032,'Switch to ')}{getChainShortName(fromChainId)}{TranslateString(2033,' Network')}
        </Button>
      )
    }

    const bigNumberAmount = new BigNumber(amount);
    const isValid = curBalance && amount && !Number.isNaN(Number(amount)) && bigNumberAmount.isGreaterThan(0) && !bigNumberAmount.isGreaterThan(curBalance);
    const allowed = alllowance && !alllowance.isLessThan(bigNumberAmount);

    if (!allowed) {
      return (
        <Button disabled={requested || !connectedAddress} onClick={handleApprove}>
          {requested ? `${TranslateString(1269, "Approving")}...` : `${TranslateString(316, "Approve")} ${tokenSymbol}`}
        </Button>
      )
    }

    return (
      <Button disabled={requested || !isValid} onClick={async()=>{
        try {
          onAnyRequestStart()
          const tx = await onDeposit(bigNumberAmount);
          if (tx && tx.status && tx.transactionHash) {
            setProcessedTransfer({
              fromChainId,
              toChainId,
              txId: tx.transactionHash
            });
          }
          setRequested(false)
        } catch (e) {
            console.error(e)
        }
      }}>
        {TranslateString(2037,'Transfer')}
      </Button>
    );
  }

  const renderButtonRow = () => {
    return (
      <Row>
        {renderButton()}
      </Row>
    );
  }

  const renderErrorRow = () => {
    return error ? (
      <ErrorRow>{error}</ErrorRow>
    ) : ""
  }

  return (
    <StyledContainer>
      <Content>
        {renderFromChainSelect()}
        {renderToChainSelect()}
        {renderTokenSelect()}
        {renderAddresses()}
        {renderAmount()}
        {renderTx()}
        {renderButtonRow()}
        {renderErrorRow()}
      </Content>
    </StyledContainer>
  )
}

export default Core
